<template>
  <modal-dialog
    ref="modalTarget"
    size="lg"
    is-body-block
    heading="Manage identifiers"
    subheading="Changes will apply to new statements, not existing statements."
    color="primary"
    :cancel-action-copy="cancelButtonCopy"
    :btn-label="modalOpenerBtnLabel"
    :btn-class="['btn-secondary', modalOpenerBtnClass].join(' ')"
    @modal-has-opened="resetFilters"
  >
    <template #modal-body>
      <div class="flex flex-col gap-8">
        <div class="grid grid-cols-2">
          <app-card>
            <transfer-recipient-badge
              :transfer-recipient="transferRecipient"
              size="sm"
            />
          </app-card>
        </div>

        <transfer-recipient-identifier-management
          v-if="modalTarget.isModalOpen"
          :agency-id="agencyId"
          ref="identifierManagement"
          heading="Linked identifiers"
          :transfer-recipient-id="transferRecipientId"
          @update:checkedIds="setSelectedAgencyIdentifiers"
        />
      </div>
    </template>
    <template #primary-action>
      <form-button
        class="btn-primary"
        :loading="isSavingIdentifiers"
        :disabled="isSavingIdentifiers || identifierManagement?.isPristine"
        @click="saveAgencyIdentifiers"
      >
        Save Changes
      </form-button>
    </template>
  </modal-dialog>
</template>
<script setup>
import ModalDialog from '@/components/ui/modal/ModalDialog.vue';
import AppCard from '@/components/ui/Card.vue';
import FormButton from '@/components/form/FormButton.vue';
import TransferRecipientIdentifierManagement from '@/components/transfer-recipients/TransferRecipientIdentifierManagement.vue';
import { computed, ref } from 'vue';
import {
  useSaveTransferRecipientIdentifiers,
  useTransferRecipientById,
} from '@/services/transfer-recipients/queries.js';
import TransferRecipientBadge from '@/components/transfer-recipients/TransferRecipientBadge.vue';
import { useToast } from '@/composables/useToast.js';
import { useTransferRecipientIdentifiersStore } from '@/stores/transferRecipients.js';

const { toast } = useToast();

const props = defineProps({
  agencyId: {
    type: String,
    required: true,
  },
  transferRecipientId: {
    type: String,
  },
  modalOpenerBtnClass: {
    type: String,
    default: 'btn-secondary',
  },
  modalOpenerBtnLabel: {
    type: String,
    default: 'Manage identifiers',
  },
});

const recipientId = computed(() => props.transferRecipientId);
const recipientIdentifiersStore = useTransferRecipientIdentifiersStore();

function resetFilters() {
  recipientIdentifiersStore.resetFilters();
}

const identifierManagement = ref(null);
const modalTarget = ref(null);

const { data } = useTransferRecipientById(recipientId);

const transferRecipient = computed(() => data?.value?.data);

const cancelButtonCopy = computed(() =>
  identifierManagement.value?.isPristine ? 'Back to beneficiary' : 'Discard changes'
);

// ----------------------------
// Selected agency identifiers
// ----------------------------
const agencyIdentifiers = ref([]);

const setSelectedAgencyIdentifiers = (ids) => {
  agencyIdentifiers.value = ids;
};

// ----------------
// Save Identifiers
// ----------------
const { mutate: saveIdentifiers, isPending: isSavingIdentifiers } = useSaveTransferRecipientIdentifiers();

function saveAgencyIdentifiers() {
  const options = {
    onSuccess: () => {
      toast({
        heading: 'Linked identifiers updated successfully',
        message: 'New statements will use these changes, existing statements may not.',
        color: 'success',
        icon: 'check-circle',
      });
      modalTarget.value?.closeModal();
    },
  };
  saveIdentifiers(
    {
      id: props.transferRecipientId,
      agency_identifier_ids: agencyIdentifiers.value,
      agency_id: props.agencyId,
    },
    options
  );
}
</script>
